import { useIntl, IntlShape } from 'react-intl';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Checkbox } from 'antd';
import { StyledShowListingContractChangesDrawer, StyledFooter } from './style';
import { StyledApplicationSummary } from '../../Form/Listings/ListingApplication/style';
import { DrawerProps } from '../Drawer';
import { Label } from '../../Label';
import { Button } from '../../Button';
import { Spinner } from '../../Spinner';
import { TextArea } from '../../TextArea';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import {
  ListingApplication,
  Listing,
  ApplicationProductData,
  ApplicationDeliveryCondition,
  DeliveryConditionsData,
  ListingProduct,
  FarmProduct,
  DeliveryProductReference,
} from '../../../types/ListingData';
import { getStorageUnitDisplay } from '../../../utils/storageUnitHelpers';
import { formatDateRange } from '../../../utils/dateHelpers';
import {
  GROUPED_QUALITY_MARKS_URL,
  QualityMarkOption,
  findQualityMark,
  hasRequiredQualityMark,
} from 'utils/qualityMarkHelpers';

interface TreeTableRow {
  id: number | string;
  application_products?: ApplicationProductData[];
  delivery_conditions?: ApplicationDeliveryCondition[];
  applicant_farm_id?: number;
  [key: string]: any;
}

interface DrawerShowListingContractChangesProps extends DrawerProps {
  application?: ListingApplication | TreeTableRow | null;
  listing?: Listing | null;
}

const getStorageUnitShortById = (
  unitId: number | undefined,
  listingProduct?: ListingProduct
): string => {
  if (!unitId) return '';

  if (
    listingProduct?.storage_unit &&
    listingProduct.storage_unit.id === unitId &&
    listingProduct.storage_unit
  ) {
    return getStorageUnitDisplay(listingProduct.storage_unit);
  }

  return '';
};

const formatFrequency = (
  value: number | undefined,
  unit: string | undefined,
  intl: IntlShape
): string => {
  if (!value || !unit) return '';
  const translatedUnit = intl.formatMessage({
    id: `enum.${unit.toUpperCase()}`,
  });
  return `(${value} ${translatedUnit})`;
};

const getDeliveryDateRange = (
  delivery: ApplicationDeliveryCondition | DeliveryConditionsData,
  listingDelivery: DeliveryConditionsData | null | undefined,
  listingData?: Listing | null | undefined,
  isAcceptingAll?: boolean
): string => {
  if (isAcceptingAll && listingDelivery) {
    if (listingDelivery.delivery_type === 'delivery_conditions') {
      return formatDateRange(
        listingDelivery.contract_start_date,
        listingDelivery.contract_end_date
      );
    }

    if (listingDelivery.delivery_type === 'detailed_delivery') {
      return formatDateRange(
        listingDelivery.delivery_start_date,
        listingDelivery.delivery_end_date
      );
    }

    if (listingDelivery.delivery_type === 'free_form') {
      return formatDateRange(
        listingDelivery.contract_start_date,
        listingDelivery.contract_end_date
      );
    }
  }

  if (listingDelivery) {
    if (listingDelivery.delivery_type === 'delivery_conditions') {
      return formatDateRange(
        listingDelivery.contract_start_date,
        listingDelivery.contract_end_date
      );
    }

    if (listingDelivery.delivery_type === 'detailed_delivery') {
      return formatDateRange(
        listingDelivery.delivery_start_date,
        listingDelivery.delivery_end_date
      );
    }

    if (listingDelivery.delivery_type === 'free_form') {
      const isAcceptingAllDelivery =
        ('is_accepting_all' in delivery && delivery.is_accepting_all) ||
        ('is_all_products' in delivery && delivery.is_all_products);

      if (isAcceptingAllDelivery) {
        return formatDateRange(
          listingDelivery.contract_start_date,
          listingDelivery.contract_end_date
        );
      }

      return formatDateRange(
        delivery.delivery_start_date,
        delivery.delivery_end_date
      );
    }
  }

  // Fallback to delivery dates (or empty if not available)
  return formatDateRange(
    delivery.delivery_start_date,
    delivery.delivery_end_date
  );
};

const getDeliveryInfo = (
  delivery: ApplicationDeliveryCondition | DeliveryConditionsData,
  listingData: Listing | null | undefined,
  intl: IntlShape
) => {
  const isAcceptingAll =
    ('is_accepting_all' in delivery && delivery.is_accepting_all) ||
    ('is_all_products' in delivery && delivery.is_all_products);
  const listingDeliveryConditionId =
    'listing_delivery_condition_id' in delivery
      ? delivery.listing_delivery_condition_id
      : undefined;

  let listingDelivery: DeliveryConditionsData | null | undefined = null;

  if (isAcceptingAll) {
    if (listingDeliveryConditionId && listingData?.delivery_conditions) {
      listingDelivery = listingData.delivery_conditions.find(
        (condition) => condition.id === listingDeliveryConditionId
      );
    }

    if (!listingDelivery && listingData?.delivery_conditions) {
      listingDelivery =
        listingData.delivery_conditions.find(
          (condition) => condition.delivery_type === 'free_form'
        ) || listingData.delivery_conditions[0];
    }
  } else if (listingDeliveryConditionId && listingData?.delivery_conditions) {
    listingDelivery = listingData.delivery_conditions.find(
      (condition) => condition.id === listingDeliveryConditionId
    );
  }

  const dateRange = getDeliveryDateRange(
    delivery,
    listingDelivery,
    listingData,
    isAcceptingAll
  );

  const frequency =
    isAcceptingAll &&
    listingDelivery &&
    listingDelivery.delivery_type !== 'free_form'
      ? formatFrequency(
          listingDelivery.delivery_frequency_value,
          listingDelivery.delivery_frequency_unit,
          intl
        )
      : listingDelivery && listingDelivery.delivery_type !== 'free_form'
        ? formatFrequency(
            listingDelivery.delivery_frequency_value,
            listingDelivery.delivery_frequency_unit,
            intl
          )
        : formatFrequency(
            delivery.delivery_frequency_value,
            delivery.delivery_frequency_unit,
            intl
          );

  return { dateRange, frequency, isAcceptingAll };
};

export const DrawerShowListingContractChanges = ({
  open,
  closable,
  destroyOnClose,
  onClose,
  title,
  maskClosable,
  children,
  closeIcon,
  application,
  listing,
}: DrawerShowListingContractChangesProps) => {
  const intl = useIntl();
  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);
  const [categoryProductsData, setCategoryProductsData] = useState<{
    [categoryId: number]: FarmProduct[];
  }>({});

  const { data: allQualityMarks } = useQueryApiClient({
    request: {
      url: GROUPED_QUALITY_MARKS_URL,
    },
    enabled: open && !!listing,
  });

  const applicationData = (application as ListingApplication) || null;

  const categoryIds = useMemo(() => {
    if (!listing?.products) return [];
    const ids = new Set<number>();
    listing.products.forEach((product: ListingProduct) => {
      if (product.category_id) {
        ids.add(product.category_id);
      }
    });
    return Array.from(ids);
  }, [listing?.products]);

  const { appendData: fetchCategoryProductsBatch } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/products/batch',
      disableOnMount: true,
    },
    onSuccess: (response: { [categoryId: number]: FarmProduct[] }) => {
      setCategoryProductsData(response || {});
      setCategoryProductsLoading(false);
    },
    onError: () => {
      setCategoryProductsLoading(false);
    },
  });

  useEffect(() => {
    if (!open || !listing || categoryIds.length === 0 || !applicationData) {
      setCategoryProductsData({});
      return;
    }

    setCategoryProductsLoading(true);
    setCategoryProductsData({});

    const data: { category_ids: number[]; farm_id?: number } = {
      category_ids: categoryIds,
    };

    if (applicationData.applicant_farm_id) {
      data.farm_id = applicationData.applicant_farm_id;
    }

    fetchCategoryProductsBatch(data);
  }, [open, listing, categoryIds, applicationData?.applicant_farm_id]);

  const availableProductsByCategory = categoryProductsData;

  const farmProductNames = useMemo(() => {
    const namesMap: { [key: number]: string } = {};
    Object.values(availableProductsByCategory).forEach((products) => {
      products.forEach((product) => {
        if (product.id && product.name) {
          namesMap[product.id] = product.name;
        }
      });
    });
    return namesMap;
  }, [availableProductsByCategory]);

  const getChangedProducts = useMemo(() => {
    if (!applicationData || !listing) return [];

    return (
      applicationData.application_products
        ?.filter((product) => product.offered_quantity !== null)
        .map((product) => ({
          ...product,
          listingProduct: listing.products?.find(
            (p) => p.id === product.listing_product_id
          ),
        })) || []
    );
  }, [applicationData, listing]);

  const listingProductToFarmProductMap = useMemo(() => {
    const map: { [listingProductId: string]: number | undefined } = {};
    applicationData?.application_products?.forEach((product) => {
      if (product.listing_product_id && product.farm_product_id) {
        map[product.listing_product_id] = product.farm_product_id;
      }
    });
    return map;
  }, [applicationData]);

  const getFarmProductName = useCallback(
    (listingProductId: string | undefined): string => {
      if (!listingProductId) return '';

      const farmProductId = listingProductToFarmProductMap[listingProductId];
      if (farmProductId && farmProductNames[farmProductId]) {
        return farmProductNames[farmProductId];
      }

      const listingProduct = listing?.products?.find(
        (p) => p.id === listingProductId
      );
      return listingProduct?.name || listingProduct?.category?.name || '';
    },
    [listingProductToFarmProductMap, farmProductNames, listing]
  );

  const getChangedDeliveries = useMemo(() => {
    if (!applicationData || !listing?.delivery_conditions?.length) return [];

    const listingHasStructuredDelivery = listing.delivery_conditions.some(
      (c: { delivery_type?: string }) =>
        c.delivery_type === 'delivery_conditions' ||
        c.delivery_type === 'detailed_delivery'
    );

    if (!listingHasStructuredDelivery) return [];

    const filtered =
      applicationData.delivery_conditions?.filter((delivery) => {
        let listingDelivery = delivery.listing_delivery_condition_id
          ? listing?.delivery_conditions?.find(
              (ldc) => ldc.id === delivery.listing_delivery_condition_id
            )
          : null;

        if (
          !listingDelivery &&
          delivery.is_accepting_all &&
          listing?.delivery_conditions
        ) {
          listingDelivery = listing.delivery_conditions.find(
            (condition) => condition.delivery_type === 'free_form'
          );
        }

        if (listingDelivery?.delivery_type === 'free_form') {
          return false;
        }

        const isChanged =
          delivery.is_accepting_all === false ||
          delivery.listing_delivery_condition_id === null;

        return isChanged;
      }) || [];

    return filtered;
  }, [applicationData, listing]);

  const freeFormDescription =
    listing?.delivery_conditions?.find(
      (condition) => condition.delivery_type === 'free_form'
    )?.free_form_description || null;

  const isLoading = categoryProductsLoading;

  return (
    <StyledShowListingContractChangesDrawer
      open={open}
      onClose={onClose}
      closable={closable}
      destroyOnClose={destroyOnClose}
      title={intl.formatMessage({
        id: 'listings.applications.contract_changes.title',
      })}
      maskClosable={maskClosable}
      width="40%"
      closeIcon={closeIcon}
      footer={
        <StyledFooter>
          <Button
            label={intl.formatMessage({ id: 'general.back' })}
            onClick={onClose}
          />
        </StyledFooter>
      }
    >
      <Spinner spinning={isLoading}>
        <StyledApplicationSummary>
          {!!getChangedProducts.length && (
            <div className="summary-section">
              <Label
                label={intl.formatMessage({
                  id: 'general.products',
                })}
                title={true}
              />
              <div className="products-list">
                {getChangedProducts.map((product) => {
                  const listingProduct = product.listingProduct;
                  const productName =
                    (product as any).product_name ||
                    (product.farm_product_id &&
                      farmProductNames[product.farm_product_id]) ||
                    listingProduct?.name ||
                    listingProduct?.category?.name ||
                    '';

                  const selectedProduct =
                    product.farm_product_id && listingProduct?.category_id
                      ? availableProductsByCategory[
                          listingProduct.category_id
                        ]?.find((p) => p.id === product.farm_product_id)
                      : undefined;

                  const selectedProductQualityMarkIds =
                    selectedProduct?.qualitymarks
                      ? selectedProduct.qualitymarks.map(
                          (qm) => qm.quality_mark_id
                        )
                      : [];

                  return (
                    <div
                      key={product.listing_product_id}
                      className="product-card"
                    >
                      <div className="product-header">
                        <div className="product-title">
                          <Label
                            title={true}
                            label={`${productName} (${intl.formatMessage({ id: 'listings.application.quantity' })}: ${product.offered_quantity ?? listingProduct?.required_quantity ?? ''} ${getStorageUnitDisplay(listingProduct?.storage_unit)}${listingProduct?.min_acceptable_offer_quantity ? `, ${intl.formatMessage({ id: 'listings.application.min_quantity' })}: ${listingProduct.min_acceptable_offer_quantity} ${getStorageUnitDisplay(listingProduct?.storage_unit)}` : ''})`}
                          />

                          {listingProduct?.quality_mark_ids &&
                            listingProduct.quality_mark_ids.length > 0 && (
                              <div className="quality-marks">
                                {listingProduct.quality_mark_ids.map((id) => {
                                  const qmark =
                                    findQualityMark<QualityMarkOption>(
                                      allQualityMarks,
                                      id
                                    );
                                  const hasProductQualityMark =
                                    hasRequiredQualityMark(
                                      id,
                                      selectedProductQualityMarkIds,
                                      allQualityMarks
                                    );
                                  return (
                                    <div
                                      key={id}
                                      className={`quality-mark-item ${
                                        hasProductQualityMark
                                          ? 'has-quality-mark'
                                          : 'no-quality-mark'
                                      }`}
                                    >
                                      <Checkbox checked={true} disabled />
                                      <Label label={qmark?.name || ''} />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="product-content">
                        <Label
                          label={intl.formatMessage({
                            id:
                              product.offer_type === 'full_amount'
                                ? 'listings.application.can_offer_full_amount'
                                : 'listings.application.other_amounts',
                          })}
                          bold
                        />
                        {product.offer_type === 'full_amount' ? (
                          product.offered_price !== undefined &&
                          product.offered_price !== null &&
                          String(product.offered_price) !== '' &&
                          Number(product.offered_price) > 0 && (
                            <Label
                              label={`${intl.formatMessage({ id: 'listings.application.price' })}: ${product.offered_price} EUR/${getStorageUnitShortById(product.offered_price_unit_id, listingProduct)}`}
                            />
                          )
                        ) : (
                          <>
                            <Label
                              label={`${intl.formatMessage({ id: 'listings.application.quantity' })}: ${product.offered_quantity ?? ''} ${getStorageUnitShortById(product.offered_unit_id, listingProduct)}`}
                            />
                            {product.offered_price !== undefined &&
                              product.offered_price !== null &&
                              String(product.offered_price) !== '' &&
                              Number(product.offered_price) > 0 && (
                                <Label
                                  label={`${intl.formatMessage({ id: 'listings.application.price' })}: ${product.offered_price} EUR/${getStorageUnitShortById(product.offered_price_unit_id, listingProduct)}`}
                                />
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!!getChangedDeliveries.length && (
            <div className="summary-section">
              <Label
                label={intl.formatMessage({
                  id: 'navigation.deliveries',
                })}
                title={true}
              />
              {freeFormDescription && (
                <>
                  <Label
                    className="free-form-description-label"
                    label={intl.formatMessage({
                      id: 'enum.FREE_FORM',
                    })}
                  />
                  <TextArea
                    value={freeFormDescription}
                    readOnly={true}
                    autoSize={true}
                  />
                </>
              )}
              <div className="deliveries-list">
                {getChangedDeliveries.map((delivery, index) => {
                  const { dateRange, frequency, isAcceptingAll } =
                    getDeliveryInfo(delivery, listing, intl);

                  const headerParts = [
                    dateRange !== '-' ? dateRange : null,
                    frequency?.trim() || null,
                  ].filter(Boolean) as string[];

                  return (
                    <div key={delivery.id || index} className="delivery-card">
                      <div className="delivery-header">
                        {!!headerParts.length && (
                          <div className="delivery-info">
                            <Label title={true} label={headerParts.join(' ')} />
                          </div>
                        )}
                      </div>

                      <div className="delivery-content">
                        {(() => {
                          const listingDeliveryConditionId =
                            'listing_delivery_condition_id' in delivery
                              ? delivery.listing_delivery_condition_id
                              : undefined;

                          let listingDelivery =
                            listingDeliveryConditionId &&
                            listing?.delivery_conditions
                              ? listing.delivery_conditions.find(
                                  (condition) =>
                                    condition.id === listingDeliveryConditionId
                                )
                              : null;

                          if (
                            !listingDelivery &&
                            isAcceptingAll &&
                            listing?.delivery_conditions
                          ) {
                            listingDelivery = listing.delivery_conditions.find(
                              (condition) =>
                                condition.delivery_type === 'free_form'
                            );
                          }

                          const hasFreeFormListingDelivery =
                            listing?.delivery_conditions?.some(
                              (condition) =>
                                condition.delivery_type === 'free_form'
                            );

                          const hasCustomDeliveries =
                            applicationData?.delivery_conditions?.some(
                              (appDelivery) =>
                                appDelivery.is_accepting_all === false
                            );

                          const shouldShowProductQuantitiesForFreeForm =
                            listingDelivery?.delivery_type === 'free_form' &&
                            isAcceptingAll &&
                            hasFreeFormListingDelivery &&
                            hasCustomDeliveries;

                          if (
                            listingDelivery?.delivery_type === 'free_form' &&
                            isAcceptingAll &&
                            !shouldShowProductQuantitiesForFreeForm
                          ) {
                            return (
                              <Label
                                label={intl.formatMessage({
                                  id: 'listings.application.all_products_accepted',
                                })}
                              />
                            );
                          }

                          return isAcceptingAll ? (
                            (() => {
                              const listingDeliveryConditionId =
                                'listing_delivery_condition_id' in delivery
                                  ? delivery.listing_delivery_condition_id
                                  : undefined;

                              let productsToDisplay: Array<{
                                id?: string;
                                categoryName: string;
                                quantity: number | string;
                                storageUnit: string;
                              }> = [];

                              if (
                                listingDeliveryConditionId &&
                                listing?.delivery_conditions
                              ) {
                                const listingDelivery =
                                  listing.delivery_conditions.find(
                                    (condition) =>
                                      condition.id ===
                                      listingDeliveryConditionId
                                  );

                                if (
                                  listingDelivery &&
                                  listingDelivery.products
                                ) {
                                  const conditionProducts = Array.isArray(
                                    listingDelivery.products
                                  )
                                    ? listingDelivery.products
                                    : [];

                                  const mappedProducts = conditionProducts.map(
                                    (
                                      p:
                                        | DeliveryProductReference
                                        | ListingProduct
                                    ): {
                                      id: string;
                                      categoryName: string;
                                      quantity: number | string;
                                      storageUnit: string;
                                    } | null => {
                                      let productId: string | undefined;
                                      let quantity: number | string | undefined;

                                      if ('listing_product_id' in p) {
                                        const productRef =
                                          p as DeliveryProductReference;
                                        productId =
                                          productRef.listing_product_id ??
                                          (productRef.id
                                            ? String(productRef.id)
                                            : undefined) ??
                                          productRef.listing_product?.id;
                                        quantity =
                                          productRef.delivery_quantity ??
                                          productRef.product_quantity;
                                      } else {
                                        const listingProd = p as ListingProduct;
                                        productId = listingProd.id;
                                        quantity =
                                          listingProd.required_quantity;
                                      }

                                      if (!productId) return null;

                                      const listingProduct =
                                        listing?.products?.find(
                                          (lp) => lp.id === productId
                                        );

                                      if (!listingProduct || !listingProduct.id)
                                        return null;

                                      const finalQuantity =
                                        quantity ??
                                        listingProduct.required_quantity ??
                                        0;

                                      return {
                                        id: listingProduct.id,
                                        categoryName: getFarmProductName(
                                          listingProduct.id
                                        ),
                                        quantity: finalQuantity,
                                        storageUnit:
                                          listingProduct.storage_unit?.short ||
                                          listingProduct.storage_unit?.name ||
                                          '',
                                      };
                                    }
                                  );

                                  productsToDisplay = mappedProducts.filter(
                                    (
                                      p
                                    ): p is NonNullable<
                                      (typeof mappedProducts)[0]
                                    > => {
                                      return (
                                        p !== null &&
                                        p.categoryName.trim() !== ''
                                      );
                                    }
                                  );
                                }
                              } else if (
                                'products' in delivery &&
                                Array.isArray(delivery.products)
                              ) {
                                const deliveryProducts =
                                  delivery.products as Array<{
                                    listing_product_id?: string;
                                    product_quantity?: number;
                                  }>;

                                const mappedDeliveryProducts =
                                  deliveryProducts.map((p) => {
                                    const listingProduct =
                                      listing?.products?.find(
                                        (lp) => lp.id === p.listing_product_id
                                      );

                                    if (!listingProduct || !p.product_quantity)
                                      return null;

                                    return {
                                      id: listingProduct.id,
                                      categoryName: getFarmProductName(
                                        listingProduct.id
                                      ),
                                      quantity: p.product_quantity,
                                      storageUnit:
                                        listingProduct.storage_unit?.short ||
                                        listingProduct.storage_unit?.name ||
                                        '',
                                    };
                                  });

                                productsToDisplay =
                                  mappedDeliveryProducts.filter(
                                    (
                                      p
                                    ): p is NonNullable<
                                      (typeof mappedDeliveryProducts)[0]
                                    > => {
                                      return (
                                        p !== null &&
                                        p.categoryName.trim() !== ''
                                      );
                                    }
                                  );
                              }

                              return (
                                <>
                                  {(() => {
                                    if (!!productsToDisplay.length) {
                                      return (
                                        <>
                                          {shouldShowProductQuantitiesForFreeForm && (
                                            <Label
                                              label={intl.formatMessage({
                                                id: 'listings.application.other_delivery_options',
                                              })}
                                              bold
                                            />
                                          )}
                                          <div className="delivery-products">
                                            {productsToDisplay.map(
                                              (prod, idx) => {
                                                const quantityText =
                                                  prod.quantity &&
                                                  String(
                                                    prod.quantity
                                                  ).trim() !== ''
                                                    ? `${prod.quantity} ${prod.storageUnit}`
                                                    : prod.storageUnit
                                                      ? prod.storageUnit
                                                      : '';
                                                return (
                                                  <Label
                                                    key={prod.id || idx}
                                                    label={
                                                      quantityText
                                                        ? `${prod.categoryName}: ${quantityText}`
                                                        : prod.categoryName
                                                    }
                                                  />
                                                );
                                              }
                                            )}
                                          </div>
                                        </>
                                      );
                                    }

                                    return (
                                      <Label
                                        label={intl.formatMessage({
                                          id: 'listings.application.all_products_accepted',
                                        })}
                                      />
                                    );
                                  })()}
                                </>
                              );
                            })()
                          ) : (
                            <>
                              <Label
                                label={intl.formatMessage({
                                  id: 'listings.application.other_delivery_options',
                                })}
                                bold
                              />
                              <div className="delivery-products">
                                {(() => {
                                  const customProducts = Array.isArray(
                                    delivery.products
                                  )
                                    ? (delivery.products as {
                                        listing_product_id: string;
                                        product_quantity: number;
                                      }[])
                                    : [];

                                  return customProducts
                                    .filter(
                                      (prod) =>
                                        prod.listing_product_id &&
                                        prod.product_quantity
                                    )
                                    .map((prod) => {
                                      const listingProduct =
                                        listing?.products?.find(
                                          (p) =>
                                            p.id === prod.listing_product_id
                                        );

                                      const categoryName = getFarmProductName(
                                        prod.listing_product_id
                                      );
                                      const storageUnitShort =
                                        listingProduct?.storage_unit?.short ||
                                        listingProduct?.storage_unit?.name ||
                                        '';

                                      if (
                                        !categoryName ||
                                        categoryName.trim() === ''
                                      ) {
                                        return null;
                                      }

                                      const quantityText = storageUnitShort
                                        ? `${prod.product_quantity} ${storageUnitShort}`
                                        : prod.product_quantity;

                                      return (
                                        <Label
                                          key={prod.listing_product_id}
                                          label={
                                            quantityText
                                              ? `${categoryName}: ${quantityText}`
                                              : categoryName
                                          }
                                        />
                                      );
                                    });
                                })()}
                              </div>
                            </>
                          );
                        })()}

                        {'delivery_price' in delivery &&
                          delivery.delivery_price != null && (
                            <Label
                              label={`${intl.formatMessage({ id: 'listings.application.delivery_price' })}: ${delivery.delivery_price} EUR`}
                              bold
                            />
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </StyledApplicationSummary>
      </Spinner>
    </StyledShowListingContractChangesDrawer>
  );
};
